import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import ReactPlayer from 'react-player';
import {
  Slider, IconButton, Box, Typography,
} from '@mui/material';
import {
  PlayArrow, Pause,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface Thumbnail {
  time: number;
  url: string;
}

interface Props {
  videoUrl: string;
  onTrimingSliderChange: (start: string, end: string) => void;
  onTrimDataChange: ({
    isTrimmed,
    startTime,
    endTime,
  }: {
    isTrimmed: boolean;
    startTime: string;
    endTime: string;
  }) => void;
}

const ProgressSlider = styled(Slider)(({ theme }) => ({
  height: 4,
  padding: 0,
  '& .MuiSlider-thumb': {
    width: 12,
    height: 12,
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
    },
    '&.Mui-active': {
      width: 16,
      height: 16,
    },
  },
  '& .MuiSlider-rail': {
    opacity: 0.7,
  },
  '& .MuiSlider-track': {
    border: 'none',
  },
}));

const TrimmingSlider = styled(Slider)(({ theme }) => ({
  height: '3px !important',
  padding: '6px 0 !important',
  bottom: '-6px',
  '& .MuiSlider-thumb': {
    height: '50px !important',
    width: '8px !important',
    backgroundColor: '#ffcb18 ',
    borderRadius: 0,
    marginTop: -23,
    marginLeft: 4,
    boxShadow: 'none !important',
    outline: 'none !important',
  },
  '& .MuiSlider-thumb:before': {
    height: '30px !important',
    width: '2px !important',
    backgroundColor: '#575757',
  },
  '& .MuiSlider-thumb:last-child': {
    marginLeft: -4,
  },
  '& .MuiSlider-track': {
    height: 4,
    backgroundColor: '#ffcb18 ',
    opacity: 1,
    marginTop: 0,
    border: 0,
    '&:before': {
      content: '""',
      position: 'absolute',
      height: 4,
      backgroundColor: '#ffcb18 ',
      opacity: 1,
      marginTop: '-47px',
      width: '100%',
    },
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#000',
    opacity: 0.1,
    height: '50px !important',
    transform: 'translateY(-80%) !important',
    top: '0px !important',
    borderRadius: '0px !important',
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#bfbfbf',
    height: 4,
    width: 1,
    marginTop: -3,
    opacity: 0,
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: '#000',
    color: '#fff',
    padding: '2px 4px',
    borderRadius: '4px',
    top: '-1px',
    '&:before': {
      display: 'none',
    },
  },
}));

const VideoTrimmer = ({ videoUrl, onTrimingSliderChange, onTrimDataChange }: Props) => {
  const SLIDER_WIDTH = 836;
  const THUMBNAIL_WIDTH = 41.8; // 20 thumbnails (SLIDER_WIDTH/20)
  const THUMBNAIL_COUNT = Math.floor(SLIDER_WIDTH / THUMBNAIL_WIDTH);

  const [playing, setPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
  const [trimRange, setTrimRange] = useState<[number, number]>([0, 1]);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);

  const playerRef = useRef<ReactPlayer>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const captureFrame = useCallback((
    video: HTMLVideoElement,
    index: number,
    totalDuration: number,
  ): Promise<Thumbnail | null> => new Promise((resolve) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      resolve(null);
      return;
    }

    const time = ((index * THUMBNAIL_WIDTH) / SLIDER_WIDTH) * totalDuration;

    const seekHandler = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/jpeg');
        console.log({ index, time, url });
        resolve({ time, url });
      } else {
        resolve(null);
      }
      video.removeEventListener('seeked', seekHandler);
    };

    video.addEventListener('seeked', seekHandler);
    const updatedVideo = video;
    updatedVideo.currentTime = time;
  }), [THUMBNAIL_WIDTH, SLIDER_WIDTH]);

  const generateThumbnails = useCallback(async () => {
    if (!duration) return;

    setIsGeneratingThumbnails(true);
    setThumbnails([]); // Clear existing thumbnails

    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';

    try {
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject();
        video.load();
      });

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < THUMBNAIL_COUNT; i++) {
        // eslint-disable-next-line no-await-in-loop
        const thumb = await captureFrame(video, i, duration);
        if (thumb) {
          setThumbnails((prevThumbnails) => [...prevThumbnails, thumb]);
        }
      }
    } catch (error) {
      console.error('Error generating thumbnails:', error);
    } finally {
      setIsGeneratingThumbnails(false);
    }
  }, [duration, videoUrl, captureFrame, THUMBNAIL_COUNT]);

  const formatTime = (timeInSeconds: number): string => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    const formatTwoDigits = (num: number): string => num.toString().padStart(2, '0');

    return `${formatTwoDigits(hours)}:${formatTwoDigits(minutes)}:${formatTwoDigits(seconds)}`;
  };

  useEffect(() => {
    if (duration > 0 && videoUrl) {
      onTrimDataChange({
        isTrimmed: false,
        startTime: '00:00:00',
        endTime: formatTime(duration),
      });
      generateThumbnails();
    }
  }, [duration, videoUrl, generateThumbnails, onTrimDataChange]);

  const handleProgress = (state: {
    played: number;
    playedSeconds: number;
    loaded: number;
    loadedSeconds: number
  }) => {
    const currentPlayedTime = state.playedSeconds;
    setCurrentTime(currentPlayedTime);
    const [start, end] = trimRange;
    const trimmedDuration = (end - start) * duration;
    const trimmedProgress = (currentPlayedTime - start * duration) / trimmedDuration;

    setProgress(trimmedProgress);

    if (currentPlayedTime >= end * duration) {
      setPlaying(false);
      playerRef.current?.seekTo(start);
    }
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const [start, end] = trimRange;
    const trimmedDuration = (end - start) * duration;
    const newTime = start * duration + (newValue as number) * trimmedDuration;
    setProgress(newValue as number);
    playerRef.current?.seekTo(newTime / duration);
  };

  const handleTrimRangeChange = (_: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) return;

    onTrimDataChange({
      isTrimmed: newValue[0] > 0 || newValue[1] < 1,
      startTime: `${formatTime(newValue[0] * duration)}`,
      endTime: `${formatTime(newValue[1] * duration)}`,
    });
    setTrimRange(newValue as [number, number]);
    const [newStart] = newValue as [number, number];
    playerRef.current?.seekTo(newStart);
    setProgress(0);
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          playing={playing}
          controls={false}
          onProgress={handleProgress}
          onDuration={setDuration}
          width="100%"
          height="auto"
          config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
              },
            },
          }}
        />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '2px 18px',
          background: 'linear-gradient(to top, rgb(0 0 0 / 73%) 0%, rgba(0, 0, 0, 0) 100%)',
          display: 'flex',
          flexDirection: 'column',
        }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton onClick={() => setPlaying(!playing)} sx={{ color: 'white', mr: 1 }}>
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Box sx={{ flexGrow: 1, mr: 2, height: 30 }}>
              <ProgressSlider
                value={progress}
                onChange={handleSliderChange}
                min={0}
                max={1}
                step={0.001}
                sx={{
                  color: 'white',
                  opacity: 0.7,
                  '& .MuiSlider-rail': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                  '& .MuiSlider-track': { backgroundColor: 'white' },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {formatTime(currentTime)}
              {' '}
              /
              {' '}
              {formatTime(duration)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <video ref={videoRef} crossOrigin="anonymous" src={videoUrl} style={{ display: 'none' }}>
        <track kind="captions" />
      </video>
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption">
          Start:
          {' '}
          {formatTime(trimRange[0] * duration)}
        </Typography>
        <Typography variant="caption">
          End:
          {' '}
          {formatTime(trimRange[1] * duration)}
        </Typography>
      </Box>

      <Box sx={{
        position: 'relative',
        height: '50px',
        mt: 3,
        width: `${SLIDER_WIDTH}px`,
        overflow: 'hidden',
      }}
      >
        {isGeneratingThumbnails ? (
          <Box sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          >
            Generating thumbnails...
          </Box>
        ) : (
          thumbnails.map((thumbnail, index) => (
            <img
              crossOrigin="anonymous"
              key={`thumb-${thumbnail.time}`}
              src={thumbnail.url}
              alt={`Thumbnail ${index}`}
              style={{
                position: 'absolute',
                left: `${index * THUMBNAIL_WIDTH}px`,
                height: '50px',
                width: `${THUMBNAIL_WIDTH}px`,
              }}
            />
          ))
        )}
        <TrimmingSlider
          value={trimRange}
          onChange={handleTrimRangeChange}
          valueLabelDisplay="on"
          step={0.001}
          marks
          min={0}
          max={1}
          disableSwap
          valueLabelFormat={(value) => formatTime(value * duration)}
          sx={{
            position: 'absolute',
            width: '100%',
            bottom: -6,
            zIndex: 9999,
          }}
        />
      </Box>
    </>
  );
};

export default VideoTrimmer;
